import axios, { type AxiosInstance } from 'axios';

import { getUserData } from '../constants/global';
import { type IPeerPresence, type IPeerThreadSummary } from '../interface/peer-chat';
import {
	type IOwnershipContext,
	type ISupportAttachment,
	type ISupportMessage,
	type ISupportReplyContext,
	type SupportSenderSide,
} from '../interface/support-chat';
import { resolveVesrBearer } from './vesr-auth';

// Standalone client for the support desk, modelled on feedback-api.ts.
//
// Deliberately NOT routed through the Redux thunk helpers, for two reasons that would each
// be enough on their own:
//
//  1. `cancelRequest()` in store/actions/utils.ts holds ONE module-level cancel source, so
//     every new request aborts the previous one. Chat polls alongside a result upload; going
//     through that layer would have the two killing each other.
//  2. Those helpers pass `timeout: timeout || 0`, and axios reads 0 as INFINITE. A dropped
//     socket on a field network leaves the promise pending forever — neither onSuccess nor
//     onFailure ever fires, and the composer spins with nothing to recover from.
//
// Every call here carries its own explicit timeout.

const VESRAPI = process.env.REACT_APP_BASE_VESR;

/** Text sends are small; 15s is generous and still bounded. */
const TEXT_TIMEOUT_MS = 15_000;

/**
 * Uploads scale with size but cap at 5 minutes, not 10. An agent staring at a spinner for
 * ten minutes has already given up and tapped Send again — a shorter ceiling surfaces the
 * failure while they are still watching.
 */
export const uploadTimeoutForSize = (bytes: number): number => Math.min(Math.max(30_000, (bytes / 20_000) * 1000), 300_000);

const client: AxiosInstance = axios.create({ baseURL: `${VESRAPI}/api/v1/vesr/support` });

client.interceptors.request.use(async (config) => {
	// VESR-issued token when we have one, core JWT otherwise — the same single-flight
	// resolution the incident hub uses, so the two never mint competing tokens.
	const bearer = await resolveVesrBearer();
	if (bearer) config.headers.Authorization = `Bearer ${bearer}`;
	return config;
});

// There is deliberately NO response interceptor.
//
// Two reasons. First, re-rejecting is axios's default, so one would be pure ceremony.
// Second and more importantly: this client must NOT carry a 401 -> logout interceptor, the
// same posture as feedbackApi and vesrApi. A support-service outage signing a field agent
// out mid-election would be catastrophic and entirely self-inflicted -- the composer
// surfaces the failure and the outbox keeps the message.
//
// Callers read error.response directly.

export interface ISupportThreadSummary {
	key: string;
	partyId: number;
	agentUserId: number;
	lastMessageAt?: string;
	lastMessagePreview?: string;
	lastMessageFrom?: 'agent' | 'desk';
	unreadForAgent: number;
	unreadForAdmins: number;
	status: string;
}

/** Opens or re-opens this agent's thread. The server resolves the caller to their own key. */
export const startSupportThread = async (): Promise<ISupportThreadSummary> => {
	const { data } = await client.post('/threads', {}, { timeout: TEXT_TIMEOUT_MS });
	return data;
};

/**
 * HTTP fallback for the hub heartbeat.
 *
 * <p>Needed because the socket is exactly what is unreliable in the conditions where
 * reachability matters most — a collation centre on a congested cell, a network that blocks
 * WebSockets and forces long-polling. Losing presence there would mark the agents hardest to
 * reach as the ones least worth trying.</p>
 *
 * <p>Never throws: presence is a secondary signal and a failed heartbeat must not surface
 * anywhere near the conversation.</p>
 */
export const sendSupportHeartbeat = async (): Promise<void> => {
	try {
		await client.post('/heartbeat', {}, { timeout: TEXT_TIMEOUT_MS });
	} catch {
		// Nothing to do and nothing to say: the next tick tries again.
	}
};

export const fetchSupportMessages = async (
	threadKey: string,
	page = 1,
	pageSize = 40
): Promise<{ messages: ISupportMessage[]; totalCount: number }> => {
	const { data } = await client.get(`/threads/${encodeURIComponent(threadKey)}/messages`, {
		params: { page, pageSize },
		timeout: TEXT_TIMEOUT_MS,
	});

	return {
		messages: (data?.data ?? []).map(normalizeMessage),
		totalCount: data?.pagination?.totalCount ?? 0,
	};
};

export interface ISendSupportMessageRequest {
	clientId: string;
	body: string;
	attachments?: ISupportAttachment[];
	/** The message this one answers, when it answers one. */
	replyToId?: string;
}

export const sendSupportMessage = async (threadKey: string, request: ISendSupportMessageRequest): Promise<ISupportMessage> => {
	const { data } = await client.post(`/threads/${encodeURIComponent(threadKey)}/messages`, request, {
		// The client id also travels as a header so the server can dedupe before it parses
		// the body — the same idempotency contract the incident comments use.
		headers: { 'X-Idempotency-Key': request.clientId },
		timeout: TEXT_TIMEOUT_MS,
	});
	return normalizeMessage(data);
};

export const uploadSupportAttachment = async (
	threadKey: string,
	file: File,
	onProgress?: (percent: number) => void
): Promise<ISupportAttachment> => {
	const form = new FormData();
	form.append('file', file);

	const { data } = await client.post(`/threads/${encodeURIComponent(threadKey)}/attachments`, form, {
		timeout: uploadTimeoutForSize(file.size),
		onUploadProgress: (event) => {
			if (!onProgress || !event.total) return;
			onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
		},
	});

	// The server refuses an attachment with no public id, but guard here too: without one a
	// later "delete for everyone" could not destroy the bytes, and we would rather fail the
	// send than persist something undeletable.
	if (!data?.cloudinaryPublicId) {
		throw new Error('The photo did not finish uploading. Attach it again.');
	}

	return data;
};

export const editSupportMessage = async (messageId: string, body: string): Promise<void> => {
	await client.put(`/messages/${encodeURIComponent(messageId)}`, { body }, { timeout: TEXT_TIMEOUT_MS });
};

/**
 * Retract for everyone.
 *
 * Answers 409 when the message has been escalated to an incident — its attachments have been
 * copied onto the incident record, so destroying the source would leave election evidence
 * pointing at nothing. The caller surfaces that as its own message rather than a generic
 * failure.
 */
export const deleteSupportMessage = async (messageId: string): Promise<void> => {
	await client.delete(`/messages/${encodeURIComponent(messageId)}`, { timeout: TEXT_TIMEOUT_MS });
};

// ───────────────────────────────────────────────────────────────────────────
// Agent ↔ agent (peer) threads. Same base, same bearer, same posture: non-2xx rejects and the
// caller reads `error.response.status`. A 404 here means EITHER the pair is not parent and
// child OR the server does not have the feature — the engine maps both to `unavailable`-shaped
// honesty rather than guessing, and the inbox probe (`fetchPeerThreads`) is what tells the two
// apart: a server with the feature answers that one with 200 and an empty page.
// ───────────────────────────────────────────────────────────────────────────

export interface IStartPeerThreadRequest {
	counterpartCoreUserId: number;
	electionId: number;
}

/** Open (or re-open) the thread between the caller and a direct parent or child. */
export const startPeerThread = async (request: IStartPeerThreadRequest): Promise<IPeerThreadSummary> => {
	const { data } = await client.post('/peer/threads', request, { timeout: TEXT_TIMEOUT_MS });
	return normalizePeerThread(data);
};

export interface IPeerThreadPage {
	threads: IPeerThreadSummary[];
	totalCount: number;
}

/** The caller's peer threads for an election, newest activity first. */
export const fetchPeerThreads = async (electionId: number, page = 1, pageSize = 100): Promise<IPeerThreadPage> => {
	const { data } = await client.get('/peer/threads', {
		params: { electionId, page, pageSize },
		timeout: TEXT_TIMEOUT_MS,
	});
	const rows: unknown[] = Array.isArray(data?.data) ? data.data : [];
	return {
		threads: rows.map((row) => normalizePeerThread(row as Record<string, unknown>)),
		totalCount: typeof data?.pagination?.totalCount === 'number' ? data.pagination.totalCount : rows.length,
	};
};

/** Up to 500 ids; the server drops any that are not the caller's proven parent or children. */
export const PEER_PRESENCE_MAX_IDS = 500;

/**
 * Presence for the caller's counterparts. A POST because a roster of ids does not fit in a
 * URL — the admin desk measured HTTP 414 at 999 ids, reported by the browser as an opaque
 * CORS error. Never throws: presence is a secondary signal.
 */
export const fetchPeerPresence = async (electionId: number, counterpartIds: number[]): Promise<IPeerPresence[]> => {
	if (counterpartIds.length === 0) return [];
	try {
		const { data } = await client.post(
			'/peer/presence',
			{ electionId, counterpartIds: counterpartIds.slice(0, PEER_PRESENCE_MAX_IDS) },
			{ timeout: TEXT_TIMEOUT_MS }
		);
		const rows: unknown[] = Array.isArray(data?.data) ? data.data : [];
		return rows.map((row) => {
			const r = row as Record<string, unknown>;
			return {
				agentUserId: Number(r.agentUserId ?? 0),
				lastSeenAt: typeof r.lastSeenAt === 'string' ? r.lastSeenAt : null,
				inThreadKey: typeof r.inThreadKey === 'string' ? r.inThreadKey : null,
			};
		});
	} catch {
		return [];
	}
};

const normalizeParticipant = (raw: unknown, role: 'parent' | 'child'): IPeerThreadSummary['parent'] => {
	const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
	return {
		coreUserId: Number(r.coreUserId ?? 0),
		fullName: typeof r.fullName === 'string' ? r.fullName : '',
		entityCode: typeof r.entityCode === 'string' ? r.entityCode : '',
		role: r.role === 'parent' || r.role === 'child' ? r.role : role,
	};
};

/** Coerce a `PeerThreadResponseDto` into the client shape; tolerant of a sparse legacy body. */
export const normalizePeerThread = (raw: Record<string, unknown>): IPeerThreadSummary => {
	const unreadRaw = raw.unread && typeof raw.unread === 'object' ? (raw.unread as Record<string, unknown>) : {};
	const unread: Record<string, number> = {};
	for (const [id, n] of Object.entries(unreadRaw)) unread[id] = typeof n === 'number' ? n : Number(n) || 0;

	const counterpart = raw.counterpart ? normalizeParticipant(raw.counterpart, 'child') : null;

	return {
		key: typeof raw.key === 'string' ? raw.key : '',
		kind: 'peer',
		partyId: Number(raw.partyId ?? 0),
		electionId: Number(raw.electionId ?? 0),
		parent: normalizeParticipant(raw.parent, 'parent'),
		child: normalizeParticipant(raw.child, 'child'),
		counterpart,
		lastSequence: Number(raw.lastSequence ?? 0),
		lastMessageAt: typeof raw.lastMessageAt === 'string' ? raw.lastMessageAt : null,
		lastMessagePreview: typeof raw.lastMessagePreview === 'string' ? raw.lastMessagePreview : null,
		lastMessageFrom: raw.lastMessageFrom === 'parent' || raw.lastMessageFrom === 'child' ? raw.lastMessageFrom : null,
		lastMessageByCoreUserId: typeof raw.lastMessageByCoreUserId === 'number' ? raw.lastMessageByCoreUserId : null,
		unreadForMe: typeof raw.unreadForMe === 'number' ? raw.unreadForMe : null,
		unread,
		counterpartLastSeenAt: typeof raw.counterpartLastSeenAt === 'string' ? raw.counterpartLastSeenAt : null,
		counterpartInThreadKey: typeof raw.counterpartInThreadKey === 'string' ? raw.counterpartInThreadKey : null,
		status: typeof raw.status === 'string' ? raw.status : 'Open',
	};
};

export const markSupportRead = async (threadKey: string, upToSequence: number): Promise<void> => {
	await client.post(`/threads/${encodeURIComponent(threadKey)}/read`, { upToSequence }, { timeout: TEXT_TIMEOUT_MS });
};

/**
 * Coerce a wire message into the client shape.
 *
 * `state` is not on the wire — anything the server returned has, by definition, been sent.
 * Delivery beyond that is reported by separate events, and the merge only ever moves state
 * forward, so seeding `sent` here cannot downgrade a message already known to be read.
 */
export const normalizeMessage = (raw: Record<string, unknown>, fallbackThreadKey?: string): ISupportMessage => ({
	id: raw.id as string,
	clientId: (raw.clientId as string) ?? undefined,
	threadId: (raw.threadKey as string) ?? fallbackThreadKey ?? '',
	senderId: (raw.senderCoreUserId as number) ?? 0,
	senderName: (raw.senderName as string) ?? '',
	senderSide: normalizeSide(raw.senderSide),
	sequence: (raw.sequence as number) ?? 0,
	kind: ((raw.kind as string) ?? 'text') as ISupportMessage['kind'],
	body: (raw.body as string) ?? '',
	attachments: (raw.attachments as ISupportAttachment[]) ?? [],
	state: raw.readByOther ? 'read' : 'sent',
	createdAt: (raw.createdAt as string) ?? new Date().toISOString(),
	editedAt: (raw.editedAt as string) ?? null,
	readByOther: !!raw.readByOther,
	deletedAt: (raw.deletedAt as string) ?? null,
	deletedBy: (raw.deletedBy as ISupportMessage['deletedBy']) ?? null,
	// Preserved as three states, not two. `undefined` (this server predates replies) must not
	// collapse into `null` (this message is not a reply): the composer withholds the Reply action
	// on the first reading, and would offer a silent no-op on the second.
	replyToId: 'replyToId' in raw ? ((raw.replyToId as string) ?? null) : undefined,
	replyTo: 'replyTo' in raw ? normalizeReplyContext(raw.replyTo) : undefined,
	context: normalizeContext(raw.context),
});

/** Only a context naming a result counts; anything else is treated as no context. */
const normalizeContext = (raw: unknown): ISupportMessage['context'] => {
	if (!raw || typeof raw !== 'object') return null;
	const c = raw as Record<string, unknown>;
	if (typeof c.resultId !== 'string' || !c.resultId) return null;
	return {
		resultId: c.resultId,
		level: typeof c.level === 'string' ? c.level.toUpperCase() : '',
		entityCode: typeof c.entityCode === 'string' && c.entityCode ? c.entityCode : null,
	};
};

const SENDER_SIDES: readonly SupportSenderSide[] = ['agent', 'desk', 'parent', 'child'];

/** Only the four sides the server can name; anything else is treated as absent. */
const normalizeSide = (raw: unknown): SupportSenderSide | undefined =>
	typeof raw === 'string' && (SENDER_SIDES as readonly string[]).includes(raw) ? (raw as SupportSenderSide) : undefined;

const normalizeReplyContext = (raw: unknown): ISupportReplyContext | null => {
	if (!raw || typeof raw !== 'object') return null;
	const r = raw as Record<string, unknown>;
	return {
		id: (r.id as string) ?? '',
		sequence: (r.sequence as number) ?? 0,
		// A support server that predates peer chat only ever says agent or desk; keep that
		// collapse for anything unrecognised so an old quote still draws.
		senderSide: normalizeSide(r.senderSide) ?? 'desk',
		senderCoreUserId: typeof r.senderCoreUserId === 'number' ? r.senderCoreUserId : null,
		kind: ((r.kind as string) ?? 'text') as ISupportReplyContext['kind'],
		body: (r.body as string) ?? '',
		hasAttachments: !!r.hasAttachments,
		isDeleted: !!r.isDeleted,
		createdAt: (r.createdAt as string) ?? '',
	};
};

/**
 * Does the backend we are talking to return a reply target once it has stored one?
 *
 * <p>The key is what carries the answer: api.vesr serializes nulls, so a server that knows about
 * replies sends `"replyToId": null` on a message that is not one, and one that does not sends no
 * such key. `null` means "cannot tell" — an empty thread carries no evidence, which never matters
 * in practice because there is nothing in one to reply to.</p>
 */
export const repliesSupported = (messages: { replyToId?: string | null }[]): boolean | null =>
	messages.length === 0 ? null : messages[0].replyToId !== undefined;

/**
 * Whether a message is the reader's own, for bubble alignment, ticks and the action sheet.
 *
 * <p>Three tests, in this order, and the order is the contract:</p>
 * <ol>
 *   <li><b>`senderId === me`</b> — the only test that works once both participants are agents,
 *   because `senderSide` is then `parent`/`child` and says nothing about which of them is
 *   reading. Skipped while the server leaves `senderCoreUserId` empty (every support build so
 *   far) or `me` is unknown.</li>
 *   <li><b>`senderSide === 'agent'`</b>, support threads only — derived from the thread key, so it
 *   survives a reload. A session-local id set does NOT: after a refresh it is empty, and keying
 *   off it made every message the agent ever sent render as if the desk had sent it.</li>
 *   <li><b>`ownClientIds`</b> — only for a message that has not round-tripped yet. Never primary:
 *   the desk mints client ids too, so "has a clientId" is not evidence of ownership.</li>
 * </ol>
 */
export const isOwnMessage = (message: ISupportMessage, ctx: IOwnershipContext): boolean => {
	if (message.senderId && ctx.me) return message.senderId === ctx.me;
	if (ctx.kind === 'support' && message.senderSide) return message.senderSide === 'agent';
	return !!message.clientId && !!ctx.ownClientIds?.has(message.clientId);
};

/**
 * The same question for a quoted message. A quote carries no clientId, so there is no third
 * test; on a peer thread talking to a server that omits `senderCoreUserId` the answer is "not
 * mine", which draws the counterpart's name — wrong for a self-quote, flagged upstream.
 */
export const isOwnReply = (
	reply: Pick<ISupportReplyContext, 'senderSide' | 'senderCoreUserId'>,
	ctx: IOwnershipContext
): boolean => {
	if (reply.senderCoreUserId && ctx.me) return reply.senderCoreUserId === ctx.me;
	if (ctx.kind === 'support') return reply.senderSide === 'agent';
	return false;
};
